/** Parse a rider's display name into surname / first name (European order in UI). */
export function parseRiderName(full: string): { surname: string; firstName: string; initials: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    const s = parts[0] ?? '';
    return { surname: s, firstName: '', initials: s.slice(0, 2).toUpperCase() };
  }
  const surname = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(' ');
  // Display order is surname-first, so initials lead with surname initial, then first-name initial.
  const initials = `${surname[0] ?? ''}${parts[0][0] ?? ''}`.toUpperCase();
  return { surname, firstName, initials };
}

const AVATAR_GRADIENTS: { gradient: string; text: string }[] = [
  { gradient: 'linear-gradient(140deg,#E4322B,#1A1A1A)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#16A8C7,#0E5C8A)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#E2402E,#A8160C)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#2C7BE5,#1B4F9B)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#15B5A0,#0E6E5E)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#FF5BA0,#D11A6A)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#1E3A6E,#0E1F40)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#1FA3D6,#0E5C8A)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#FFE600,#E0A400)', text: '#3A2A00' },
  { gradient: 'linear-gradient(140deg,#1A2A52,#0E1A38)', text: '#fff' },
  { gradient: 'linear-gradient(140deg,#16294F,#0B1530)', text: '#fff' }
];

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Deterministic avatar colours per rider name. */
export function riderAvatarStyle(name: string): { background: string; color: string } {
  const { gradient, text } = AVATAR_GRADIENTS[hashName(name) % AVATAR_GRADIENTS.length];
  return { background: gradient, color: text };
}

/** Extract a padded stage number from labels like "Stage 1" or "Gameweek 12". */
export function stageNumberFromLabel(label: string): string {
  const m = label.match(/\d+/);
  if (!m) return '';
  return m[0].padStart(2, '0');
}

/**
 * Flag emoji from an ISO 3166-1 alpha-2 country code (e.g. "SI" → 🇸🇮). Returns
 * an empty string for anything that isn't a clean 2-letter code, so a missing or
 * malformed nationality just renders nothing rather than tofu glyphs.
 */
export function flagEmoji(code: string | null | undefined): string {
  if (!code) return '';
  const cc = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(base + (cc.charCodeAt(0) - 65), base + (cc.charCodeAt(1) - 65));
}

/** A compact "Team · 🇸🇮" subtitle for a rider, omitting whichever part is missing. */
export function riderSubtitle(teamName?: string | null, nationality?: string | null): string {
  const flag = flagEmoji(nationality);
  const parts = [teamName?.trim() || '', flag].filter(Boolean);
  return parts.join('  ');
}

/**
 * Parse a ProCyclingStats cumulative-time string into total seconds. Handles
 * "MM:SS", "H:MM:SS" and the big grand-tour / team totals ("83:22:51",
 * "250:42:41"), tolerating a leading "+". Returns null for anything that isn't a
 * real clock value (e.g. an empty cell or a same-time ",," marker).
 */
export function parseCyclingTimeSeconds(time: string | null | undefined): number | null {
  if (!time) return null;
  const t = time.trim().replace(/^\+/, '');
  if (!/^\d+(:\d{2}){1,2}$/.test(t)) return null;
  return t.split(':').reduce((acc, part) => acc * 60 + Number(part), 0);
}

/**
 * The cycling gap-to-leader cell for a classification row. The leader carries the
 * reference total time; everyone else shows how far back they are the way cycling
 * always does it — "+0:34", "+2:22", "+1:07:41" — with same-time riders marked
 * "s.t." (same time). Returns the raw time if it can't be parsed, so nothing breaks.
 */
export function cyclingGapLabel(
  rowTime: string | null | undefined,
  leaderSeconds: number | null,
  isLeader: boolean
): string {
  if (isLeader) return rowTime?.trim() || '—';
  const secs = parseCyclingTimeSeconds(rowTime);
  if (secs == null || leaderSeconds == null) return rowTime?.trim() || '—';
  const gap = Math.max(0, Math.round(secs - leaderSeconds));
  if (gap === 0) return 's.t.';
  const h = Math.floor(gap / 3600);
  const m = Math.floor((gap % 3600) / 60);
  const s = String(gap % 60).padStart(2, '0');
  return h > 0 ? `+${h}:${String(m).padStart(2, '0')}:${s}` : `+${m}:${s}`;
}
